import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft, Bold, Italic, Underline, Strikethrough, List, ListOrdered,
  Undo2, Redo2, Plus, FileText, Mic, Image as ImageIcon, Camera, Trash2,
  NotebookPen, Loader2, X, Square, Paperclip, PenSquare,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ReceiptViewer from "@/components/dashboard/ReceiptViewer";

const fmtCardDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" }); } catch { return ""; }
};

// Samsung-Notes-style fallback label: "Text note MM-DD" from the created date.
const noteLabel = (n) => {
  if ((n.title || "").trim()) return n.title.trim();
  try {
    const d = new Date(n.created_at);
    return `Text note ${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch { return "Text note"; }
};

const FONT_SIZES = [
  { label: "14", px: 14 },
  { label: "17", px: 17 },
  { label: "22", px: 22 },
  { label: "28", px: 28 },
];

/**
 * ReferenceTab (shown as "Notes") — a pocket notepad (Android-notepad-style)
 * for every user.
 * Card grid of past notes; tapping one opens the editor with a basic
 * formatting toolbar (B / I / U / strike / lists / size / undo / redo) and
 * an attach menu limited to PDF, voice recording, image and camera.
 */
const ReferenceTab = () => {
  const [notes, setNotes] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notes");
      setNotes(Array.isArray(data) ? data : []);
    } catch { setNotes([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const newNote = async () => {
    try {
      const { data } = await api.post("/notes", { title: "", content: "" });
      setOpenId(data.note_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the note");
    }
  };

  if (openId) {
    return (
      <NoteEditor
        noteId={openId}
        onBack={() => { setOpenId(null); load(); }}
      />
    );
  }

  return (
    <div className="relative min-h-[60vh]" data-testid="reference-tab">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 text-[#4f6fe8]">
          <NotebookPen className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Notes</h3>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">
            Your private notepad — job details, codes, measurements, anything
          </p>
        </div>
      </div>

      {notes === null ? (
        <p className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/40">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading notes…
        </p>
      ) : notes.length === 0 ? (
        <button
          onClick={newNote}
          data-testid="note-empty-new"
          className="mt-6 flex w-full flex-col items-center gap-2 rounded-lg border border-dashed border-white/20 bg-white/[0.02] px-4 py-10 text-white/50 hover:border-[#1a3fd1] hover:text-white/80"
        >
          <PenSquare className="h-6 w-6" />
          <span className="text-sm">No notes yet — tap to write your first one</span>
        </button>
      ) : (
        <div className="mt-4 columns-1 gap-4 sm:columns-2 lg:columns-3" data-testid="notes-grid">
          {notes.map((n) => (
            <div key={n.note_id} className="mb-5 break-inside-avoid">
              <button
                onClick={() => setOpenId(n.note_id)}
                data-testid={`note-card-${n.note_id}`}
                className="block w-full rounded-xl border border-white/10 bg-white/[0.04] p-4 text-left shadow-sm transition-colors hover:border-[#1a3fd1]/60 hover:bg-white/[0.06]"
              >
                <p className="whitespace-pre-line text-sm leading-relaxed text-white/85 line-clamp-[10]">
                  {n.snippet || <span className="text-white/35">Empty note</span>}
                </p>
                {(n.attachments || []).length > 0 && (
                  <p className="mt-2 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0]">
                    <Paperclip className="h-3 w-3" /> {n.attachments.length} file{n.attachments.length === 1 ? "" : "s"}
                  </p>
                )}
              </button>
              <p className="mt-2 text-center text-sm font-semibold text-white/90" data-testid={`note-label-${n.note_id}`}>{noteLabel(n)}</p>
              <p className="mt-0.5 text-center text-xs text-white/45">{fmtCardDate(n.updated_at)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Floating new-note button (notepad-style compose) */}
      <button
        onClick={newNote}
        aria-label="New note"
        data-testid="note-new"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#1a3fd1] text-white shadow-lg shadow-black/40 transition-transform hover:scale-105 hover:bg-[#2c5bf0]"
      >
        <PenSquare className="h-6 w-6" />
      </button>
    </div>
  );
};

const NoteEditor = ({ noteId, onBack }) => {
  const [note, setNote] = useState(null);
  const [title, setTitle] = useState("");
  const [saveState, setSaveState] = useState("saved"); // saved | unsaved | saving
  const [attachOpen, setAttachOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [attUrls, setAttUrls] = useState({}); // attachment_id -> object/data URL
  const [receiptSrc, setReceiptSrc] = useState(null);

  const editorRef = useRef(null);
  const saveTimer = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const pdfInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/notes/${noteId}`);
        setNote(data);
        setTitle(data.title || "");
        if (editorRef.current) editorRef.current.innerHTML = data.content || "";
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not open the note");
        onBack();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId]);

  const saveNow = useCallback(async () => {
    setSaveState("saving");
    try {
      await api.put(`/notes/${noteId}`, {
        title,
        content: editorRef.current?.innerHTML || "",
      });
      setSaveState("saved");
    } catch {
      setSaveState("unsaved");
      toast.error("Could not save the note");
    }
  }, [noteId, title]);

  const scheduleSave = useCallback(() => {
    setSaveState("unsaved");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(saveNow, 700);
  }, [saveNow]);

  // Flush pending save when leaving.
  const goBack = async () => {
    clearTimeout(saveTimer.current);
    if (saveState !== "saved") await saveNow();
    onBack();
  };

  const exec = (cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    scheduleSave();
  };

  const applyFontSize = (px) => {
    editorRef.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("fontSize", false, "7");
    // Chrome still emits <font size=7>; swap those for real px spans.
    editorRef.current?.querySelectorAll('font[size="7"]').forEach((f) => {
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      span.innerHTML = f.innerHTML;
      f.replaceWith(span);
    });
    setSizeOpen(false);
    scheduleSave();
  };

  const uploadAttachment = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/notes/${noteId}/attachments`, fd);
      setNote((n) => (n ? { ...n, attachments: [...(n.attachments || []), data] } : n));
      toast.success("File attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not attach the file");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const file = new File([blob], `voice-recording-${Date.now()}.webm`, { type: blob.type });
        await uploadAttachment(file, "audio");
      };
      mr.start();
      mediaRef.current = mr;
      setRecSeconds(0);
      setRecording(true);
      recTimerRef.current = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone unavailable — check permission and try again");
    }
  };

  const stopRecording = () => {
    clearInterval(recTimerRef.current);
    setRecording(false);
    try { mediaRef.current?.stop(); } catch { /* already stopped */ }
  };

  const loadAttachment = async (att) => {
    if (attUrls[att.attachment_id]) return attUrls[att.attachment_id];
    const { data } = await api.get(`/notes/${noteId}/attachments/${att.attachment_id}`);
    // Convert to a Blob URL — data: URLs are blocked by window.open and
    // some Android WebViews choke on giant data: strings in <audio>.
    const blob = await (await fetch(data.data_url)).blob();
    const url = URL.createObjectURL(blob);
    setAttUrls((m) => ({ ...m, [att.attachment_id]: url }));
    return url;
  };

  const openAttachment = async (att) => {
    try {
      if (att.kind === "image") {
        const url = await loadAttachment(att);
        setReceiptSrc(url);
      } else if (att.kind === "pdf") {
        const url = await loadAttachment(att);
        window.open(url, "_blank");
      }
    } catch {
      toast.error("Could not load the attachment");
    }
  };

  const removeAttachment = async (att) => {
    try {
      await api.delete(`/notes/${noteId}/attachments/${att.attachment_id}`);
      setNote((n) => (n ? { ...n, attachments: (n.attachments || []).filter((a) => a.attachment_id !== att.attachment_id) } : n));
      toast.success("Attachment removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove the attachment");
    }
  };

  const deleteNote = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusyDelete(true);
    try {
      clearTimeout(saveTimer.current);
      await api.delete(`/notes/${noteId}`);
      toast.success("Note deleted");
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the note");
      setBusyDelete(false);
    }
  };

  const fmtRec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const ToolBtn = ({ onClick, label, testid, children }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault() /* keep editor selection */}
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid={testid}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white"
    >
      {children}
    </button>
  );

  return (
    <div className="flex min-h-[70vh] flex-col" data-testid="note-editor">
      {/* Top bar — back, title, attach, delete */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-3">
        <button onClick={goBack} aria-label="Back to notes" data-testid="note-back"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-white/15 bg-white/5 text-white/70 hover:text-white">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <input
          value={title}
          onChange={(e) => { setTitle(e.target.value); scheduleSave(); }}
          placeholder="Title"
          data-testid="note-title"
          className="h-9 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 text-base font-semibold text-white placeholder:text-neutral-400 focus:border-white/15 focus:bg-white/5 focus:outline-none"
        />
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="note-save-state">
          {saveState === "saving" ? "Saving…" : saveState === "unsaved" ? "Unsaved" : "Saved"}
        </span>
        {/* Attach (+) lives in the TOP bar like the reference layout. Tapping
            it blurs the editor first so the on-screen keyboard drops instead
            of popping up over the menu. */}
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => { document.activeElement?.blur?.(); setAttachOpen((v) => !v); }}
            aria-label="Add a file"
            data-testid="attach-menu"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-white/15 bg-white/5 text-white/70 hover:text-white"
          >
            <Plus className="h-4 w-4" />
          </button>
          {attachOpen && (
            <div className="absolute right-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-md border border-white/15 bg-[#0b0b0f] py-1 shadow-xl" data-testid="attach-menu-popover">
              {[
                { key: "pdf", label: "PDF", Icon: FileText, fn: () => pdfInputRef.current?.click() },
                { key: "voice", label: "Voice recording", Icon: Mic, fn: startRecording },
                { key: "image", label: "Image", Icon: ImageIcon, fn: () => imageInputRef.current?.click() },
                { key: "camera", label: "Camera", Icon: Camera, fn: () => cameraInputRef.current?.click() },
              ].map(({ key, label, Icon, fn }) => (
                <button key={key} type="button"
                  onClick={() => { setAttachOpen(false); fn(); }}
                  data-testid={`attach-${key}`}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-white/80 hover:bg-white/10">
                  <Icon className="h-4 w-4 text-[#8fa4f0]" /> {label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={deleteNote} disabled={busyDelete} aria-label="Delete note" data-testid="note-delete"
          className={`flex h-9 shrink-0 items-center gap-1 rounded-md border px-2 text-xs ${
            confirmDelete
              ? "border-red-500/60 bg-red-500/15 text-red-600 dark:text-red-400"
              : "border-white/15 bg-white/5 text-white/70 hover:text-red-400"
          }`}>
          {busyDelete ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          {confirmDelete && <span>Sure?</span>}
        </button>
      </div>

      {/* Content */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={scheduleSave}
        data-testid="note-content"
        className="min-h-[40vh] flex-1 px-1 py-4 text-[17px] leading-relaxed text-white/90 focus:outline-none [&:empty]:before:text-neutral-400 [&:empty]:before:content-['Start_typing…']"
      />

      {/* Attachments */}
      {(note?.attachments || []).length > 0 && (
        <div className="mb-3 space-y-2" data-testid="note-attachments">
          {note.attachments.map((att) => (
            <div key={att.attachment_id} data-testid={`note-attachment-${att.attachment_id}`}
              className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs">
              {att.kind === "image" ? <ImageIcon className="h-4 w-4 shrink-0 text-[#8fa4f0]" />
                : att.kind === "pdf" ? <FileText className="h-4 w-4 shrink-0 text-red-400" />
                : <Mic className="h-4 w-4 shrink-0 text-emerald-400" />}
              {att.kind === "audio" ? (
                attUrls[att.attachment_id] ? (
                  <audio controls src={attUrls[att.attachment_id]} className="h-8 min-w-0 flex-1" data-testid={`note-audio-${att.attachment_id}`} />
                ) : (
                  <button onClick={() => loadAttachment(att).catch(() => toast.error("Could not load the recording"))}
                    data-testid={`note-audio-load-${att.attachment_id}`}
                    className="min-w-0 flex-1 truncate text-left text-white/70 hover:text-white">
                    {att.name} — tap to load
                  </button>
                )
              ) : (
                <button onClick={() => openAttachment(att)} data-testid={`note-attachment-open-${att.attachment_id}`}
                  className="min-w-0 flex-1 truncate text-left text-white/70 hover:text-white">
                  {att.name}
                </button>
              )}
              <button onClick={() => removeAttachment(att)} aria-label="Remove attachment" data-testid={`note-attachment-del-${att.attachment_id}`}
                className="rounded border border-white/15 bg-white/5 p-1 text-white/60 hover:text-red-400">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Recording bar */}
      {recording && (
        <div className="mb-3 flex items-center gap-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2" data-testid="voice-recording-bar">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
          <span className="font-mono text-xs text-red-700 dark:text-red-300">{fmtRec(recSeconds)}</span>
          <span className="min-w-0 flex-1 text-xs text-white/60">Recording…</span>
          <button onClick={stopRecording} data-testid="voice-stop"
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500">
            <Square className="h-3 w-3" /> Stop &amp; attach
          </button>
        </div>
      )}

      {/* Bottom toolbar — formatting + attach. The popovers MUST live outside
          the overflow-x-auto scroller or it clips them (they were invisible). */}
      <div className="sticky bottom-0 -mx-1 flex items-center border-t border-white/10 bg-[#0b0b0f] px-1 py-1.5" data-testid="note-toolbar">
        <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
          <ToolBtn onClick={() => exec("undo")} label="Undo" testid="fmt-undo"><Undo2 className="h-4 w-4" /></ToolBtn>
          <ToolBtn onClick={() => exec("redo")} label="Redo" testid="fmt-redo"><Redo2 className="h-4 w-4" /></ToolBtn>
          <span className="mx-1 h-5 w-px shrink-0 bg-white/10" />
          <ToolBtn onClick={() => exec("bold")} label="Bold" testid="fmt-bold"><Bold className="h-4 w-4" /></ToolBtn>
          <ToolBtn onClick={() => exec("italic")} label="Italic" testid="fmt-italic"><Italic className="h-4 w-4" /></ToolBtn>
          <ToolBtn onClick={() => exec("underline")} label="Underline" testid="fmt-underline"><Underline className="h-4 w-4" /></ToolBtn>
          <ToolBtn onClick={() => exec("strikeThrough")} label="Strikethrough" testid="fmt-strike"><Strikethrough className="h-4 w-4" /></ToolBtn>
          <span className="mx-1 h-5 w-px shrink-0 bg-white/10" />
          <ToolBtn onClick={() => exec("insertOrderedList")} label="Numbered list" testid="fmt-ol"><ListOrdered className="h-4 w-4" /></ToolBtn>
          <ToolBtn onClick={() => exec("insertUnorderedList")} label="Bulleted list" testid="fmt-ul"><List className="h-4 w-4" /></ToolBtn>
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setSizeOpen((v) => !v)}
            data-testid="fmt-size"
            className="flex h-9 items-center gap-1 rounded-md px-2 font-mono text-xs text-white/70 hover:bg-white/10 hover:text-white"
          >
            17 <span className="text-[9px]">▼</span>
          </button>
          {sizeOpen && (
            <div className="absolute bottom-full right-0 z-20 mb-1 overflow-hidden rounded-md border border-white/15 bg-[#0b0b0f] shadow-xl" data-testid="fmt-size-menu">
              {FONT_SIZES.map((s) => (
                <button key={s.px} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyFontSize(s.px)}
                  data-testid={`fmt-size-${s.px}`}
                  className="block w-full px-4 py-1.5 text-left text-white/80 hover:bg-white/10"
                  style={{ fontSize: s.px }}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hidden file inputs for the attach menu */}
      <input ref={pdfInputRef} type="file" accept="application/pdf" className="hidden" data-testid="attach-pdf-input"
        onChange={(e) => { uploadAttachment(e.target.files?.[0], "pdf"); e.target.value = ""; }} />
      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" data-testid="attach-image-input"
        onChange={(e) => { uploadAttachment(e.target.files?.[0], "image"); e.target.value = ""; }} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="attach-camera-input"
        onChange={(e) => { uploadAttachment(e.target.files?.[0], "image"); e.target.value = ""; }} />

      <ReceiptViewer src={receiptSrc} onClose={() => setReceiptSrc(null)} />
    </div>
  );
};

export default ReferenceTab;
